/** Minimal credentials needed to cancel a prior hold (ticket/secret optional). */
export type StationarySlotHoldCredentials = {
  slotId: number;
  ticket?: string | null;
  secret?: string | null;
};

/**
 * Pure reserve/replace strategy for stationary slot holds.
 *
 * 1) Prefer reserve-first so a failed replace keeps the previous bay held.
 * 2) Fallback when the API rejects concurrent holds: cancel previous, then reserve.
 *    If this reserve fails, the previous hold is already gone — `onPreviousHoldLost`
 *    fires so the caller drops it instead of checking out on a cancelled reservation.
 */
export async function reserveStationarySlotWithReplaceStrategy<THold>(args: {
  previousHold?: StationarySlotHoldCredentials | null;
  reserve: () => Promise<THold>;
  cancelHold: (hold: StationarySlotHoldCredentials) => Promise<void>;
  /** The fallback released `previousHold` and the replacement failed too: the caller holds nothing. */
  onPreviousHoldLost?: () => void;
}): Promise<THold> {
  const { previousHold, reserve, cancelHold, onPreviousHoldLost } = args;

  // Settle the reserve on its own so a throwing cancel below can never fall
  // through to the replace path, which would reserve twice and orphan the hold.
  const reserved = await reserve().then(
    (held) => ({ ok: true as const, held }),
    (error: unknown) => ({ ok: false as const, error })
  );

  if (!reserved.ok) {
    if (!previousHold) {
      throw reserved.error;
    }
    // API rejected the concurrent hold: release the old bay, then take the new one.
    await cancelHold(previousHold);
    try {
      return await reserve();
    } catch (error) {
      onPreviousHoldLost?.();
      throw error;
    }
  }

  // Releasing the old bay is best-effort - the new hold is what the caller needs.
  if (previousHold) {
    await cancelHold(previousHold).catch(() => undefined);
  }
  return reserved.held;
}
